// EXTERNAL DEPENDENCIES
import React, { useEffect } from 'react'
import { useDispatch } from 'react-redux'

// HELPERS
import { updateIsEditing, updateIsPreviewing } from '@/redux/features'
import { trackImpression } from '@/utils'
import {
  useBusinessProfilePageContent,
  useBusinessProfileReduxState,
} from '../../hooks'

// COMPONENTS
import { Button } from '@paypalcorp/pp-react'
import ProfileCard from '@/common/components/ui/profile/profile-card'
import { PageHeaderSection } from '../'

// TYPES
import {
  LocationDisplayPreference,
  PortableAddress,
  UnformattedFptiOptions,
} from '@/types'
import { ProfileCardProps } from '@/common/components/ui/profile/profile-card/types'

// TEST IDS
import { PREVIEW_PROFILE_PAGE_TEST_ID } from '../../constants/test-ids'

// FPTI
import {
  PREVIEW_PROFILE_BACK_LINK_CLICK_FPTI,
  PREVIEW_PROFILE_SHARE_BUTTON_CLICK_FPTI,
} from './fpti'

// STYLES
import styles from './styles.module.less'

/**
 * Allows the profile owner to see what their profile looks like
 * to the buyer or what their changes will look like when saved
 */
export function PreviewProfilePage({
  userCanCreateProfile,
  CustomFooterButtons,
  stateToPreview = {},
  impressionToTrack,
  subHeaderText,
  customBackLinkText,
  backLinkFpti,
  userCanEdit,
  handleBackClick,
}: {
  userCanCreateProfile?: boolean
  CustomFooterButtons?: ProfileCardProps['CardFooterButtons']
  stateToPreview?: object
  impressionToTrack?: UnformattedFptiOptions
  subHeaderText?: string
  customBackLinkText?: string
  backLinkFpti?: string
  userCanEdit?: boolean
  handleBackClick?: () => void
}) {
  const dispatch = useDispatch()
  const getContent = useBusinessProfilePageContent()
  const editProfileText = getContent('editProfile')

  useEffect(() => {
    if (impressionToTrack?.eventName) {
      trackImpression(impressionToTrack)
    }
  }, [impressionToTrack])

  // STATE
  const { profileData = {} } = useBusinessProfileReduxState()
  const {
    address,
    avatarUrl,
    backgroundUrl,
    customerCount,
    handle,
    businessJoinDate,
    businessName,
    businessDescription,
    profilePreferences,
    emailAddress: businessEmail,
    phoneNumber: businessPhoneNumber,
    website: businessWebsiteUrl,
  } = profileData

  const DefaultFooterButtons = ({
    handleShareProfileClick,
  }: Parameters<ProfileCardProps['CardFooterButtons']>[0]) => (
    <>
      <Button secondary={userCanEdit} onClick={handleShareProfileClick}>
        {getContent('shareProfile')}
      </Button>
      {userCanEdit && (
        <Button
          primary
          onClick={() => {
            dispatch(updateIsPreviewing(false))
            dispatch(updateIsEditing(true))
          }}
        >
          {editProfileText}
        </Button>
      )}
    </>
  )

  return (
    <div data-testid={PREVIEW_PROFILE_PAGE_TEST_ID}>
      <PageHeaderSection
        customBackLinkText={customBackLinkText}
        handleBackClick={() => {
          handleBackClick?.()
          dispatch(updateIsPreviewing(false))
        }}
        subHeaderText={subHeaderText}
        pageHeaderText={getContent('previewProfilePageHeader')}
        backLinkFpti={backLinkFpti || PREVIEW_PROFILE_BACK_LINK_CLICK_FPTI}
      />
      <div className={styles.profileContainer}>
        <ProfileCard
          isPreviewing
          userOwnsProfile
          userCanCreateProfile={userCanCreateProfile}
          isLoading={!businessName}
          CardFooterButtons={CustomFooterButtons || DefaultFooterButtons}
          fpti={{ shareLinkFpti: PREVIEW_PROFILE_SHARE_BUTTON_CLICK_FPTI }}
          data={{
            businessName,
            businessDescription,
            businessHandle: handle,
            businessAddress: address as PortableAddress,
            accountCreateDate: businessJoinDate,
            pastYearCustomerCount: customerCount,
            contactInfo: {
              businessEmail,
              businessPhoneNumber,
              businessWebsiteUrl,
            },
            coverPhotoSrc: backgroundUrl,
            profilePhotoSrc: avatarUrl,
            selectedLocationDisplayPreference:
              profilePreferences?.locationDisplayPreference as LocationDisplayPreference,
            ...stateToPreview,
          }}
        />
      </div>
    </div>
  )
}
